import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useCurrentPage } from '@/components/Page';

export const panelViewModes = ['resources', 'fileTree', 'database'] as const;
export type PanelViewMode = (typeof panelViewModes)[number];

type NavPanelState = {
  navMode: PanelViewMode;
  updateNavMode: (mode: PanelViewMode) => void;
};

const NavPanelContext = createContext<NavPanelState>({
  navMode: panelViewModes[0],
  updateNavMode: () => {},
});

export const useNavPanel = () => {
  return useContext(NavPanelContext);
};

export const NavPanelProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const { currentPage } = useCurrentPage();
  const [isUserSelected, setIsUserSelected] = useState(false);

  const defaultOption = useMemo<PanelViewMode>(() => {
    if (currentPage === 'databaseDetails') return 'database';
    return panelViewModes[0];
  }, [currentPage]);

  const [navMode, setNavMode] = useState<PanelViewMode>(defaultOption);

  // Update the nav mode to match the page type, until a user makes a manual selection
  if (
    !isUserSelected &&
    currentPage !== 'projectOverview' &&
    navMode !== defaultOption
  ) {
    setNavMode(defaultOption);
  }

  const updateNavMode = (mode: PanelViewMode) => {
    setIsUserSelected(true);
    setNavMode(mode);
  };

  return (
    <NavPanelContext.Provider value={{ navMode, updateNavMode }}>
      {children}
    </NavPanelContext.Provider>
  );
};
